/**
 * useActiveAssignments — Single source of truth for "is there work
 * waiting for this learner?".
 *
 * Audit ref: LEARNER_HOME_FLOW_UX_AUDIT_2026 §P1-3.
 *
 * Before this hook, the same `Promise.all([getTeacherAssignments, getPracticeAssignments])`
 * filter pipeline was inlined in three places:
 *   - HomePage.tsx          (banner rendering, group-filtered)
 *   - Sidebar.tsx           (oranje dot op het Toetsen nav-item)
 *   - MobileNav.tsx         (zelfde dot, mobile)
 *
 * Each maintained its own `now` check, `completedBy` filter and `wholeClass`
 * logic — drift-risico bij elke regel-change. This hook consolidates the
 * pipeline; callers filter further by group if needed.
 *
 * Note: this is a one-shot fetch, no polling. It refetches when the
 * `learnerId` changes (account-switch) and exposes a `revalidate()` for
 * callers that want to refresh after a known mutation (e.g. completing an
 * assignment elsewhere in the session).
 */

import { useCallback, useEffect, useState } from 'react';
import { dataLayer } from '../../data/dataLayer';
import { useStore } from '../store';
import { getLearnerExistingClassrooms } from '../../accounts';
import type { TeacherAssignment, PracticeAssignment } from '../../assessment';
export interface ActiveAssignments {
  /** All active teacher-assessment assignments for this learner (any group). */
  assessments: TeacherAssignment[];
  /** All active practice assignments for this learner (any group). */
  practiceAssignments: PracticeAssignment[];
  /** Convenience: true if either list has at least one entry. */
  hasAny: boolean;
  /** True while the first fetch is in flight. */
  isLoading: boolean;
  /** Force a refetch (e.g. after the learner completes work elsewhere). */
  revalidate: () => void;
}
const EMPTY: Pick<ActiveAssignments, 'assessments' | 'practiceAssignments' | 'hasAny'> = {
  assessments: [],
  practiceAssignments: [],
  hasAny: false
};
/**
 * Hoort dit werk bij deze leerling?
 *
 * ── De klasgrens ontbrak hier volledig ────────────────────────────────────
 *
 * `wholeClass` gaf onvoorwaardelijk `true` terug, zonder ooit naar
 * `assignment.classroomId` te kijken. Een leerkracht met twee klassen die
 * huiswerk voor "hele klas" klaarzette, stuurde dat dus naar **elke leerling
 * die aan hem gekoppeld is** — ook de kinderen uit zijn andere klas. En omdat
 * co-docenten bij het toevoegen van een leerling aan álle leerlingen van de
 * klas worden gekoppeld, kon het verder reiken dan de eigen klassen.
 *
 * Het gemene zit in de asymmetrie: het leerkrachtscherm filtert zijn eigen
 * lijst wél op `classroomId` (`HomeworkPage`), dus de leerkracht ziet netjes
 * één klas en gelooft dat hij één klas heeft bereikt. De aflevering aan het
 * kind negeerde dat veld. Er is geen melding, geen fout — een kind uit groep 6
 * krijgt gewoon het werk van groep 8 in zijn scherm, en de leerkracht ziet
 * later een score die daar niet hoort.
 *
 * `classroomId === null` betekent bewust "niet aan een klas gebonden" en blijft
 * voor iedereen gelden: dat is wat een opdracht is die zonder klasselectie is
 * gemaakt, en die achteraf inperken zou bestaand werk laten verdwijnen.
 *
 * De leerling-ids worden NIET tegen de klas gecontroleerd wanneer ze expliciet
 * zijn aangewezen (`targetStudentIds`). Een leerkracht die één kind aanwijst,
 * heeft dat kind zelf gekozen; dat is geen ongericht bereik.
 */
function isActiveFor(assignment: {
  deadline?: string | number | Date | null;
  completedByStudentIds?: string[];
  wholeClass?: boolean;
  targetStudentIds?: string[];
  classroomId?: string | null;
}, learnerId: string, now: Date, learnerClassroomIds: string[]): boolean {
  if (assignment.deadline && new Date(assignment.deadline) < now) return false;
  if (assignment.completedByStudentIds?.includes(learnerId)) return false;
  // Expliciet aangewezen leerlingen eerst: een gerichte opdracht hoort aan te
  // komen, ook als het kind inmiddels van klas is gewisseld.
  if (assignment.targetStudentIds?.includes(learnerId)) return true;
  if (assignment.wholeClass) {
    const classroomId = assignment.classroomId ?? null;
    if (classroomId === null) return true;
    return learnerClassroomIds.includes(classroomId);
  }
  return false;
}
export function useActiveAssignments(learnerId: string | null | undefined): ActiveAssignments {
  const [data, setData] = useState<Pick<ActiveAssignments, 'assessments' | 'practiceAssignments' | 'hasAny'>>(EMPTY);
  const [isLoading, setIsLoading] = useState<boolean>(Boolean(learnerId));
  const [version, setVersion] = useState(0);
  const revalidate = useCallback(() => setVersion(v => v + 1), []);
  useEffect(() => {
    if (!learnerId) {
      setData(EMPTY);
      setIsLoading(false);
      return;
    }
    let cancelled = false;
    setIsLoading(true);
    Promise.all([dataLayer.getTeacherAssignments(), dataLayer.getPracticeAssignments()]).then(([allAssessments, allPractice]) => {
      if (cancelled) return;
      const now = new Date();
      // Klaslidmaatschap uit de store, niet uit een prop: dit is de enige plek
      // waar het bereik van "hele klas" wordt bepaald, en een aanroeper die
      // vergeet het mee te geven zou de grens stil weer openzetten.
      const accounts = useStore.getState().multiAccountState?.accounts;
      const learnerClassroomIds = accounts ? getLearnerExistingClassrooms(accounts, learnerId).map(c => c.classroom.id) : [];
      const assessments = Object.values(allAssessments).filter(a => isActiveFor(a, learnerId, now, learnerClassroomIds));
      const practiceAssignments = Object.values(allPractice).filter(a => isActiveFor(a, learnerId, now, learnerClassroomIds));
      setData({
        assessments,
        practiceAssignments,
        hasAny: assessments.length > 0 || practiceAssignments.length > 0
      });
    }).catch(() => {
      if (!cancelled) setData(EMPTY);
    }).finally(() => {
      if (!cancelled) setIsLoading(false);
    });
    return () => {
      cancelled = true;
    };
  }, [learnerId, version]);
  return {
    ...data,
    isLoading,
    revalidate
  };
}